import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Search } from "lucide-react";
import { toast } from "react-toastify";
import "../../style/transfer-history.css";
import BackIcon from "../../assets/icons/BackIcon";
import TransferHistoryPos from "../../assets/transfer-history-pos.svg";
import NoDataIcon from "../../assets/icons/NoDataIcon";
import { stockTransferAPI } from "../../services";

const TransferHistoryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);

  const [transfer, setTransfer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [stats, setStats] = useState({
    total_items: 0,
    total_quantity: 0,
  });
  const [allItems, setAllItems] = useState([]);

  // Fetch transfer detail from API
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchTransferDetail = async () => {
      if (!id) return;
      
      setLoading(true);
      try {
        const params = {};
        if (searchTerm.trim()) {
          params.search = searchTerm.trim();
        }

        const response = await stockTransferAPI.getTransferSummary(id, {
          params,
          signal: controller.signal,
        });

        if (!isMounted) return;

        const data = response.data || response;
        setTransfer(data);
        setAllItems(data.items || []);
        setStats({
          total_items: data.total_products || 0,
          total_quantity: data.total_quantity || 0,
        });
      } catch (error) {
        if (!isMounted || error.name === "CanceledError") return;
        console.error("Failed to fetch transfer detail:", error);
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          error?.message ||
          "Failed to load transfer detail";
        toast.error(message);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    const timeoutId = setTimeout(fetchTransferDetail, searchTerm ? 300 : 0);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [id, searchTerm]);

  // Filter items based on search (client-side filtering for displayed items)
  const filteredItems = allItems;

  // Pagination
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredItems.slice(startIndex, startIndex + itemsPerPage);

  if (loading) {
    return (
      <div className="transfer-detail-loading">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!transfer) {
    return (
      <div className="transfer-detail-error">
        <h3>Transfer not found</h3>
        <button onClick={() => navigate("/transfer-history")}>
          Back to Transfer History
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="back-navigation">
        <button
          className={`${isPartsURL ? "back-button-parts" : isAccessoriesURL ? "back-button-acc" : "back-button"}`}
          onClick={() => navigate("/transfer-history")}
        >
          <BackIcon fill={isPartsURL ? "var(--primary-color-parts)" : isAccessoriesURL ? "var(--primary-color-acc)" : "var(--primary-color-mobile)"} />
          Back to Transfer History
        </button>
      </div>
      <div className="card rounded-4 p-4 border-0 transfer-detail-container-new transfer-detail-layout">
        <div className="transfer-detail-top">
          {/* header */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              <h2 className="mrph-txt size-22 weight-600 color-text-medium mb-1">
                Transfer to POS
              </h2>
              <p className="mrph-txt size-14 weight-400 color-text-semi-regular">
                Move stock from godown to POS for retail or bulk selling
              </p>
            </div>
          </div>

          {/* stats */}
          <div className="row mb-4">
            <div className="col-md-4">
              <div
                className="card mrph-border border brands-summary-card"
                style={{ borderRadius: "10px", height: "92px" }}
              >
                <div className="card-body d-flex align-items-start justify-content-between">
                  <div className="d-flex flex-column align-items-start justify-content-center gap-2">
                    <h6 className="mrph-txt size-14 weight-400 color-text-semi-regular mb-0">
                      Total Models
                    </h6>
                    <h4 className="mrph-txt size-22 weight-600 color-text-medium mb-0">
                      {loading ? (
                        <div
                          className="spinner-border spinner-border-sm"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      ) : (
                        stats.total_items
                      )}
                    </h4>
                  </div>
                  <img src={TransferHistoryPos} alt="Total Models" />
                </div>
              </div>
            </div>
          </div>

          {/* search/filter */}
          <div className="d-flex align-items-center mb-3 gap-3">
            <div className="position-relative flex-grow-1">
              <input
                type="text"
                className={`${isPartsURL
                    ? "transfer-search-input-parts"
                    : isAccessoriesURL
                      ? "transfer-search-input-acc"
                      : "transfer-search-input"
                  } text-search`}
                placeholder="Search by model, tag or category"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Search
                size={16}
                className="position-absolute top-50 start-0 translate-middle-y ms-3"
                style={{ color: "#8C959F" }}
              />
            </div>
          </div>
        </div>

        {/* table */}
        <div className="transfer-detail-table-container-new transfer-detail-table-wrapper">
          <table className="transfer-detail-table-new">
            <thead style={{ backgroundColor: "#F6F8FA" }}>
              <tr>
                <th>MODEL</th>
                <th>TAG</th>
                <th>CATEGORY</th>
                <th>SELLING PRICE</th>
                <th>QUANTITY</th>
              </tr>
            </thead>
            <tbody className="transfer-detail-tbody">
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-4">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan="6" className="no-products-message">
                    <div
                      className=""
                      style={{
                        marginBottom: "4rem",
                        marginTop: "4rem",
                      }}
                    >
                      <NoDataIcon />
                      <h3 style={{ marginTop: "2rem" }}>No items found</h3>
                    </div>
                  </td>
                </tr>
              ) : (
                currentItems.map((item) => (
                  <tr
                    key={item.id}
                    className="mrph-txt size-14 color-text-regular"
                    style={{ height: "60px" }}
                  >
                    <td className="px-3">
                      {item.product_name || "-"}
                    </td>
                    <td className="px-3">{item.unique_identifier_identifier || "-"}</td>
                    <td className="px-3">{item.product_brand || "-"}</td>
                    <td className="px-3">
                      {typeof item.selling_price === "number"
                        ? `€${item.selling_price.toFixed(2)}`
                        : "-"}
                    </td>
                    <td className="px-3">{item.quantity || 0}</td>

                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-4 transfer-detail-pagination-sticky">
            <div className="mrph-txt size-14 color-text-semi-regular">
              Page {currentPage} of {totalPages}
            </div>
            <div className="pagination-controls">
              <button
                className={`${isPartsURL
                    ? "pagination-btn-parts"
                    : isAccessoriesURL
                      ? "pagination-btn-acc"
                      : "pagination-btn"
                  }`}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              >
                Previous
              </button>
              <button
                className={`${isPartsURL
                    ? "pagination-btn-next-parts"
                    : isAccessoriesURL
                      ? "pagination-btn-next-acc"
                      : "pagination-btn-next"
                  }`}
                disabled={currentPage >= totalPages}
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TransferHistoryDetail;

